const commaRegex = /(\d)(?=(\d{3})+$)/g;
const leadZeroesRegex = /^0+/;
const trailZeroesRegex = /0+$/;

const doCommaRegex = (val: string, commas: boolean) => {
  return commas ? val.replace(commaRegex, '$1,') : val;
};

const processDecimal = (val: string, commas: boolean, paddedZeroes?: number) => {
  const numArr = []; // push left side, right side, and join with '.'
  const vals = val.split('.');

  // if blank, use a 0 (ex. 0.9), otherwise use the commaregex
  numArr.push(vals[0] ? doCommaRegex(vals[0], commas) : '0');

  // if split results in 2 items, process right side of num
  if (vals.length === 2) {
    const noTrailZeroes = vals[1].replace(trailZeroesRegex, '');
    let padding = paddedZeroes || noTrailZeroes ? 0 : 1;
    if (paddedZeroes && noTrailZeroes) {
      // pad zeroes
      // note: if noTrailZeroes is greater than paddedZeroes, it will create negative padding:
      //                                                  0        -     10 = -10
      padding = noTrailZeroes.length < paddedZeroes ? paddedZeroes - noTrailZeroes.length : 0;
    }
    numArr.push(`${noTrailZeroes}${'0'.repeat(padding)}`);
  } else {
    numArr.push(paddedZeroes ? '0'.repeat(paddedZeroes) : '0');
  }

  // join (ex ['2343', '34] -> 2343.34)
  return numArr.join('.');
};

type FormatNumberInputType = string | number | undefined | null;

export const formatNumber = (val: FormatNumberInputType, commas = false, decimals = false, paddedZeroes?: number) => {
  // return empty if not valid or empty
  // number 0 should be parsed as the formatted '0'
  if (val === '' || val === undefined || val === null || Number.isNaN(Number(val))) {
    return '';
  }

  // '00000' => '0'
  const trimmedVal = val.toString().replace(leadZeroesRegex, '');
  if (!trimmedVal) {
    return '0';
  }

  if (decimals) {
    return processDecimal(trimmedVal, commas, paddedZeroes);
  }
  return doCommaRegex(trimmedVal, commas);
};

// ex: 0.00034
export const formatRcf = (val: FormatNumberInputType) => {
  return formatNumber(val, false, true, 5);
};

// used for apr rates. ex: 0.9
export const formatDecimal = (val: FormatNumberInputType, commas = true) => {
  return formatNumber(val, commas, true);
};

// ex: 1,203
export const formatDollars = (val: FormatNumberInputType, commas = true, decimals = false) => {
  return formatNumber(val, commas, decimals);
};

export const formatMillions = (val: FormatNumberInputType, round: boolean, tofixed = 2) => {
  const milionValue = Number(val) / 1_000_000;

  if (round) {
    return milionValue.toFixed(tofixed);
  }

  const stringValue = milionValue.toString();
  return stringValue.slice(0, stringValue.indexOf('.') + 3);
};

// ex: 1,200.92
export const formatDollarsCents = (val: FormatNumberInputType, commas = true) => {
  return formatNumber(val, commas, true, 2);
};

export const formatAccounting = (val: FormatNumberInputType, symbol = true, includeCents = true) => {
  const num = includeCents ? formatDollarsCents(val) : formatDollars(val);
  const isNeg = num.indexOf('-') !== -1;
  const dollarSign = symbol ? '$' : '';
  return isNeg ? `(${dollarSign}${num.replace('-', '')})` : `${dollarSign}${num}`;
};
